import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/Common";
import { UploadCloud, Loader2, PackageCheck, PackageX, PackageSearch, AlertTriangle, Truck, Filter, Download, CheckCircle2, XCircle, Clock3, Printer, Mail, Send, Archive, ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

const VERDICT_META = {
  on_time:      { label: "On time",     color: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  late:         { label: "LATE",        color: "bg-rose-50 text-rose-800 border-rose-200",           Icon: XCircle },
  in_transit:   { label: "In transit",  color: "bg-sky-50 text-sky-800 border-sky-200",              Icon: Truck },
  not_found:    { label: "Not found",   color: "bg-slate-100 text-slate-700 border-slate-300",       Icon: PackageX },
  invalid:      { label: "Invalid #",   color: "bg-amber-50 text-amber-900 border-amber-200",        Icon: AlertTriangle },
  rate_limited: { label: "Rate limited",color: "bg-orange-50 text-orange-900 border-orange-200",     Icon: Clock3 },
  unknown:      { label: "Unknown",     color: "bg-slate-50 text-slate-700 border-slate-200",        Icon: PackageSearch },
};

const parseCsvDate = (s) => {
  if (!s) return null;
  const t = Date.parse(s);
  if (!isNaN(t)) return new Date(t);
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s.trim());
  return m ? new Date(+m[3], +m[1] - 1, +m[2]) : null;
};

const fmtTs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function UpsShipments() {
  const [report, setReport] = useState(null); // { count, rows, totals, warnings }
  const [filename, setFilename] = useState("");
  const [busyUpload, setBusyUpload] = useState(false);
  const [busyVerify, setBusyVerify] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState({});   // tracking_number -> result
  const [days, setDays] = useState(30);
  const [verdictFilter, setVerdictFilter] = useState("late"); // late | all | on_time | in_transit | not_found
  const [tierFilter, setTierFilter] = useState("all");        // all | 2nd_day_air | next_day_air
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);
  const [savingToVera, setSavingToVera] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const fileRef = useRef();

  const loadSavedReports = async () => {
    try {
      const { data } = await api.get("/ups-shipments/saved");
      setSavedReports(data.reports || []);
    } catch (e) { console.warn("saved ups reports load failed", e); }
  };
  useEffect(() => { loadSavedReports(); }, []);

  const saveToVera = async () => {
    if (!visibleRows.length) return toast.error("Nothing to save — no rows match the current filter");
    setSavingToVera(true);
    try {
      await api.post("/ups-shipments/save", { ...buildLatePayload(), filename });
      toast.success("Saved to Vera — pull it back up from Saved reports anytime");
      await loadSavedReports();
      setShowSavedList(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingToVera(false); }
  };

  const downloadSaved = async (id, savedAt) => {
    try {
      const res = await api.get(`/ups-shipments/saved/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `vera-shipping-report-${(savedAt || "").slice(0, 10)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this saved shipping report? This cannot be undone.")) return;
    try {
      await api.delete(`/ups-shipments/saved/${id}`);
      toast.success("Saved shipping report deleted");
      setSavedReports((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const upload = async (file) => {
    setReport(null); setResults({}); setBusyUpload(true); setFilename(file.name);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ups-shipments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      setReport(data);
      const skipMsg = (data.warnings || []).join(" · ");
      toast.success(`${data.count} 2nd-Day-Air tracking numbers ready to verify${skipMsg ? " · " + skipMsg : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusyUpload(false); }
  };

  const eligible = useMemo(() => {
    if (!report?.rows) return [];
    // Always exclude stale rows (>120 days) — UPS won't have them.
    const fresh = report.rows.filter((r) => !r.stale);
    if (days === "all") return fresh;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return fresh.filter((r) => {
      const d = parseCsvDate(r.date);
      return d ? d.getTime() >= cutoff : true;
    });
  }, [report, days]);

  const verify = async () => {
    if (!eligible.length) return toast.error("Nothing to verify");
    setBusyVerify(true); setResults({}); setProgress({ done: 0, total: eligible.length });
    const CHUNK = 25;
    const acc = {};
    try {
      for (let i = 0; i < eligible.length; i += CHUNK) {
        const chunk = eligible.slice(i, i + CHUNK).map((r) => ({
          tracking_number: r.tracking_number,
          service_tier: r.service_tier || "2nd_day_air",
        }));
        const { data } = await api.post("/ups-shipments/verify",
          { tracking_numbers: chunk }, { timeout: 90000 });
        for (const r of data.results || []) acc[r.tracking_number] = r;
        setResults({ ...acc });
        setProgress({ done: Math.min(i + CHUNK, eligible.length), total: eligible.length });
      }
      const lateCount = Object.values(acc).filter((r) => r.verdict === "late").length;
      toast.success(`Verified ${Object.keys(acc).length} · ${lateCount} LATE`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification interrupted — partial results kept");
    } finally { setBusyVerify(false); }
  };

  const merged = useMemo(() => (eligible || []).map((r) => ({
    ...r, result: results[r.tracking_number] || null,
    verdict: results[r.tracking_number]?.verdict || null,
  })), [eligible, results]);

  const lateRows = useMemo(() => merged.filter((r) => r.verdict === "late"), [merged]);
  const notFoundRows = useMemo(() => merged.filter((r) => r.verdict === "not_found"), [merged]);
  // Refund-review order:
  //   1. Next Day Air late rows (stricter SLA + shorter UPS claim window)
  //   2. 2nd Day Air late rows
  //   3. Not-found rows last
  // Within each group, sort by ship date DESCENDING (newest first).
  const applyTier = (list) => tierFilter === "all"
    ? list
    : list.filter((r) => (r.service_tier || "2nd_day_air") === tierFilter);
  const stuckRows = useMemo(() => {
    const now = Date.now();
    return merged.filter((r) => {
      if (r.verdict !== "in_transit") return false;
      const dl = r.result?.deadline_at ? Date.parse(r.result.deadline_at) : NaN;
      return !isNaN(dl) && dl < now;
    });
  }, [merged]);
  // Refund-review = late + stuck (in-transit past deadline) + not-found. Sort:
  //   1. Next Day LATE (oldest first) then Next Day STUCK (oldest first)
  //   2. 2nd Day  LATE (oldest first) then 2nd Day  STUCK (oldest first)
  //   3. Not-found (oldest first)
  const refundRows = useMemo(() => {
    const groupPriority = (r) => {
      if (r.verdict === "not_found") return 5;
      const isStuck = stuckRows.includes(r);
      const isNext = (r.service_tier || "2nd_day_air") === "next_day_air";
      if (isNext && r.verdict === "late") return 1;
      if (isNext && isStuck) return 2;
      if (!isNext && r.verdict === "late") return 3;
      if (!isNext && isStuck) return 4;
      return 9;
    };
    const shipTs = (r) => {
      const d = parseCsvDate(r.date);
      return d ? d.getTime() : 0;
    };
    return [...lateRows, ...stuckRows, ...notFoundRows].sort((a, b) => {
      const pa = groupPriority(a), pb = groupPriority(b);
      if (pa !== pb) return pa - pb;
      return shipTs(a) - shipTs(b);
    });
  }, [lateRows, stuckRows, notFoundRows]);

  const visibleRows = useMemo(() => {
    let base;
    if (verdictFilter === "all") base = merged;
    else if (verdictFilter === "late") base = refundRows;
    else if (verdictFilter === "stuck") base = stuckRows;
    else if (verdictFilter === "unverified") base = merged.filter((r) => !r.verdict);
    else if (verdictFilter === "in_transit")
      base = merged.filter((r) => r.verdict === "in_transit" && !stuckRows.includes(r));
    else base = merged.filter((r) => r.verdict === verdictFilter);
    return applyTier(base);
  }, [merged, refundRows, stuckRows, verdictFilter, tierFilter]);

  const totals = useMemo(() => {
    const now = Date.now();
    const t = { on_time: 0, late: 0, in_transit: 0, not_found: 0, invalid: 0,
                unknown: 0, rate_limited: 0, stuck: 0, verified: 0 };
    Object.values(results).forEach((r) => {
      t[r.verdict] = (t[r.verdict] || 0) + 1;
      if (r.verdict) t.verified++;
      if (r.verdict === "in_transit" && r.deadline_at) {
        const dl = Date.parse(r.deadline_at);
        if (!isNaN(dl) && dl < now) t.stuck++;
      }
    });
    return t;
  }, [results]);

  const buildLatePayload = (rows = null) => {
    // Print / email uses whatever is currently on-screen — so filtering to
    // "stuck" or "in transit" only prints those rows, not the entire refund
    // list. Falls back to refundRows for backwards compatibility.
    const source = applyTier(rows ?? visibleRows ?? refundRows);
    const filterLabel = verdictFilter === "all" ? "All verified rows"
      : verdictFilter === "late" ? "Late (refund review)"
      : verdictFilter === "stuck" ? "Stuck past deadline"
      : verdictFilter === "in_transit" ? "In-transit on-track"
      : verdictFilter === "on_time" ? "On-time deliveries"
      : verdictFilter === "not_found" ? "Not found (past retention window)"
      : verdictFilter === "unverified" ? "Unverified"
      : verdictFilter;
    return {
      late_rows: source.map((r) => ({
        date: r.date, patient: r.patient, tracking_number: r.tracking_number,
        service: r.service, service_tier: r.service_tier || "2nd_day_air",
        state: r.state,
        verdict: stuckRows.includes(r) ? "stuck" : r.verdict,
        received_at: r.result?.received_at, delivered_at: r.result?.delivered_at,
        deadline_at: r.result?.deadline_at,
        hours_in_transit: r.result?.hours_in_transit,
      })),
      meta: {
        total_verified: totals.verified,
        late_count: applyTier(lateRows).length,
        stuck_count: applyTier(stuckRows).length,
        not_found_count: applyTier(notFoundRows).length,
        filter_label: filterLabel,
        printed_count: source.length,
        range_note: (days === "all" ? "All dates" : `Last ${days} days`)
          + (tierFilter === "all" ? "" : tierFilter === "next_day_air" ? " · Next Day Air only" : " · 2nd Day Air only")
          + ` · ${filterLabel}`,
      },
    };
  };

  const printReport = async () => {
    if (!visibleRows.length) return toast.error("Nothing to print — no rows match the current filter");
    setBusyPdf(true);
    try {
      const resp = await api.post("/ups-shipments/report.pdf", buildLatePayload(),
        { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) w.focus(); else toast.success("PDF downloaded");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed");
    } finally { setBusyPdf(false); }
  };

  const emailReport = async () => {
    const to = emailTo.trim();
    if (!to) return toast.error("Enter at least one recipient email");
    if (!visibleRows.length) return toast.error("Nothing to email — no rows match the current filter");
    setSendingEmail(true);
    try {
      const { data } = await api.post("/ups-shipments/email-report",
        { ...buildLatePayload(), to }, { timeout: 60000 });
      toast.success(`Sent refund report to ${data.sent} recipient(s)`);
      setEmailOpen(false); setEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally { setSendingEmail(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6">
      <PageHeader
        title="UPS Shipment Verifier"
        subtitle="Upload the pharmacy shipping report → verify every UPS 2nd Day Air (end of 2nd business day) and Next Day Air (midnight next day) package against UPS's “We Have Your Package” scan → export a Vera-branded refund report." />

      {savedReports.length > 0 && (
        <Card className="p-3 mb-4 border-emerald-200" data-testid="ups-saved-reports-card">
          <button onClick={() => setShowSavedList((s) => !s)}
            data-testid="ups-saved-reports-toggle"
            className="w-full flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-emerald-800">
            <Archive className="w-4 h-4 text-emerald-700" />
            Saved shipping reports
            <span className="text-xs font-normal text-slate-500">({savedReports.length})</span>
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showSavedList ? "rotate-180" : ""}`} />
          </button>
          {showSavedList && (
            <div className="mt-3 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-xs">
                    <TableHead>Saved at</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Filter</TableHead>
                    <TableHead>Saved by</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                    <TableHead className="text-right">Late</TableHead>
                    <TableHead className="text-right">Stuck</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {savedReports.map((s) => (
                    <TableRow key={s.id} data-testid={`ups-saved-row-${s.id}`}>
                      <TableCell className="text-xs font-mono text-slate-700 whitespace-nowrap">
                        {(s.saved_at || "").replace("T", " ").slice(0, 16)}
                      </TableCell>
                      <TableCell className="text-xs text-slate-800">{s.filename}</TableCell>
                      <TableCell className="text-xs text-slate-600">{s.filter_label || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{s.saved_by}</TableCell>
                      <TableCell className="text-xs font-mono text-right text-slate-700">{s.shipment_count}</TableCell>
                      <TableCell className="text-xs font-mono text-right text-rose-800">{s.late_count}</TableCell>
                      <TableCell className="text-xs font-mono text-right text-amber-800">{s.stuck_count}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button data-testid={`ups-saved-download-${s.id}`} onClick={() => downloadSaved(s.id, s.saved_at)}
                          size="sm" variant="outline" className="h-7 gap-1 text-xs">
                          <Download className="w-3 h-3" /> PDF
                        </Button>
                        <Button data-testid={`ups-saved-delete-${s.id}`} onClick={() => deleteSaved(s.id)}
                          size="sm" variant="ghost" className="h-7 gap-1 text-xs text-rose-600 hover:text-rose-800 hover:bg-rose-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {/* Upload strip */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            className="hidden" data-testid="ups-upload-input" />
          <Button data-testid="ups-upload-btn" onClick={() => fileRef.current?.click()}
            disabled={busyUpload}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5">
            {busyUpload
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</>
              : <><UploadCloud className="w-4 h-4" /> Upload shipping CSV</>}
          </Button>
          {filename && <span className="text-xs text-slate-500 font-mono">{filename}</span>}
          {report && (
            <div className="ml-auto flex items-center gap-2 text-sm text-slate-600" data-testid="ups-summary">
              <span className="font-semibold text-slate-800">{report.count} eligible</span>
              <span className="text-slate-400">·</span>
              <span>{report.totals?.total_lines || 0} total lines</span>
              <span className="text-slate-400">·</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-medium">
                {report.totals?.second_day_air || 0} 2nd Day
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-xs font-medium">
                {report.totals?.next_day_air || 0} Next Day
              </span>
              {report.totals?.stale_hidden ? (
                <span data-testid="ups-stale-count"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-medium"
                  title="Older than 120 days — UPS no longer retains tracking data for these packages.">
                  {report.totals.stale_hidden} stale hidden
                </span>
              ) : null}
            </div>
          )}
        </div>
        {report?.warnings?.length ? (
          <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            {report.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        ) : null}
      </Card>

      {/* Verify controls */}
      {report && report.count > 0 && (
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600">Only verify last</span>
              {[7, 30, 60, 120, "all"].map((d) => (
                <button key={d} type="button" data-testid={`ups-days-${d}`}
                  onClick={() => setDays(d)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    days === d
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}>
                  {d === "all" ? "all dates" : `${d} days`}
                </button>
              ))}
              <span className="text-xs text-slate-400 ml-1">UPS retains data ~120 days</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-slate-700">
                <span className="font-mono font-bold text-slate-900" data-testid="ups-eligible-count">
                  {eligible.length}
                </span> to verify
              </span>
              <Button data-testid="ups-verify-btn" onClick={verify}
                disabled={busyVerify || eligible.length === 0}
                className="bg-emerald-700 hover:bg-emerald-800 h-9 gap-1.5">
                {busyVerify
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying {progress.done}/{progress.total}</>
                  : <><PackageCheck className="w-4 h-4" /> Verify with UPS</>}
              </Button>
            </div>
          </div>

          {/* Post-verify summary + refund actions */}
          {totals.verified > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Verdict:</span>
                {[
                  { k: "late",       label: `Refund review (${applyTier(refundRows).length})`, cls: "bg-rose-700 text-white border-rose-700" },
                  { k: "stuck",      label: `Stuck past deadline (${applyTier(stuckRows).length})`, cls: "bg-amber-600 text-white border-amber-600" },
                  { k: "all",        label: `All (${totals.verified})`,          cls: "bg-slate-900 text-white border-slate-900" },
                  { k: "on_time",    label: `On time (${totals.on_time})`,       cls: "bg-emerald-700 text-white border-emerald-700" },
                  { k: "in_transit", label: `In transit on-track (${Math.max(0, totals.in_transit - totals.stuck)})`, cls: "bg-sky-700 text-white border-sky-700" },
                  { k: "not_found",  label: `Not found (${totals.not_found})`,   cls: "bg-slate-600 text-white border-slate-600" },
                ].map((f) => (
                  <button key={f.k} type="button"
                    data-testid={`ups-vfilter-${f.k}`}
                    onClick={() => setVerdictFilter(f.k)}
                    className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
                      verdictFilter === f.k ? f.cls
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Service:</span>
                {[
                  { k: "all",          label: "All services",  cls: "bg-slate-900 text-white border-slate-900" },
                  { k: "2nd_day_air",  label: "UPS 2nd Day Air (end of 2nd business day)", cls: "bg-indigo-700 text-white border-indigo-700" },
                  { k: "next_day_air", label: "UPS Next Day Air (midnight next day)", cls: "bg-purple-700 text-white border-purple-700" },
                ].map((f) => (
                  <button key={f.k} type="button"
                    data-testid={`ups-tier-${f.k}`}
                    onClick={() => setTierFilter(f.k)}
                    className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
                      tierFilter === f.k ? f.cls
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}>
                    {f.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <Button data-testid="ups-print-report" onClick={printReport}
                    disabled={busyPdf || !visibleRows.length}
                    variant="outline" className="h-9 gap-1.5"
                    title={`Prints ${visibleRows.length} row${visibleRows.length === 1 ? "" : "s"} matching the current filter`}>
                    {busyPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                    Print report ({visibleRows.length})
                  </Button>
                  <Button data-testid="ups-save-to-vera" onClick={saveToVera}
                    disabled={savingToVera || !visibleRows.length}
                    variant="outline" className="h-9 gap-1.5 border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                    title={`Save ${visibleRows.length} row${visibleRows.length === 1 ? "" : "s"} to Vera`}>
                    {savingToVera ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                    Save to Vera
                  </Button>
                  <Button data-testid="ups-email-report-btn"
                    onClick={() => setEmailOpen((x) => !x)}
                    disabled={!visibleRows.length}
                    className="bg-pink-700 hover:bg-pink-800 h-9 gap-1.5"
                    title={`Emails ${visibleRows.length} row${visibleRows.length === 1 ? "" : "s"} matching the current filter`}>
                    <Mail className="w-4 h-4" /> Email report ({visibleRows.length})
                  </Button>
                </div>
              </div>
            </div>
          )}

          {emailOpen && (
            <div className="mt-3 p-3 border border-pink-200 rounded flex flex-wrap items-center gap-2"
              data-testid="ups-email-panel">
              <span className="text-xs text-slate-500">Send Vera-branded PDF to:</span>
              <Input data-testid="ups-email-input" type="text"
                placeholder="ordering@pharmacy.com, refunds@pharmacy.com"
                value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && emailReport()}
                className="h-8 text-xs flex-1 min-w-[280px]" />
              <Button data-testid="ups-email-send" size="sm" onClick={emailReport}
                disabled={sendingEmail}
                className="bg-pink-700 hover:bg-pink-800 h-8 gap-1.5">
                {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send
              </Button>
              <span className="text-[11px] text-slate-400">
                {lateRows.length} late package(s) will be attached as a PDF.
              </span>
            </div>
          )}
        </Card>
      )}

      {!report && (
        <EmptyState icon={Truck}
          title="Drop your shipping report"
          hint="Upload the CSV export from your shipping platform. Vera pulls every UPS 2nd Day Air and UPS Next Day Air row, checks each tracking number against UPS's live data, and flags every package that missed its SLA — end of the 2nd business day for 2nd Day Air (skipping weekends & UPS holidays), midnight of the next calendar day for Next Day Air. Late rows become a printable + emailable Vera refund report." />
      )}

      {report && report.count > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              {verdictFilter === "late" ? `Refund review · ${lateRows.length} late + ${stuckRows.length} stuck + ${notFoundRows.length} not-found (manual review)`
                : verdictFilter === "stuck" ? `Stuck past deadline · in transit but past SLA — proactive follow-up recommended`
                : verdictFilter === "all" ? "All verified rows"
                : verdictFilter === "on_time" ? "On-time deliveries"
                : verdictFilter === "in_transit" ? "In-transit packages"
                : verdictFilter === "not_found" ? "Not found (past retention window)"
                : "Filtered rows"}
            </span>
            <span className="text-xs text-slate-500 font-mono">
              {visibleRows.length} row{visibleRows.length === 1 ? "" : "s"}
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Tracking #</TableHead>
                <TableHead>Service</TableHead>
                {verdictFilter === "all" ? <TableHead>Verdict</TableHead> : null}
                <TableHead>Received by UPS</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.length === 0 && totals.verified > 0 && verdictFilter === "late" && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-emerald-700 py-8">
                    No late deliveries in this batch — every 2nd Day Air package hit the 48-hour window.
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.slice(0, 500).map((r, i) => {
                const v = r.verdict ? VERDICT_META[r.verdict] : null;
                const Icon = v?.Icon;
                const rowTint = r.verdict === "late" ? "bg-rose-50/40"
                  : r.verdict === "not_found" ? "bg-amber-50/40" : "";
                return (
                  <TableRow key={r.tracking_number} data-testid={`ups-row-${i}`}
                    className={`hover:bg-slate-50/60 ${rowTint}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{r.date || "—"}</TableCell>
                    <TableCell className="text-sm">{r.patient || "—"}</TableCell>
                    <TableCell className="text-xs font-mono font-semibold text-slate-700">{(r.state || "").toUpperCase() || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <a data-testid={`ups-track-link-${r.tracking_number}`}
                        href={`https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(r.tracking_number)}`}
                        target="_blank" rel="noopener noreferrer"
                        title="Open UPS tracking in a new tab"
                        className="text-blue-800 hover:text-blue-950 underline decoration-blue-300 hover:decoration-blue-800">
                        {r.tracking_number}
                      </a>
                    </TableCell>
                    <TableCell className="text-xs">
                      {(r.service_tier || "2nd_day_air") === "next_day_air" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-800 border border-purple-200">
                          Next Day Air
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200">
                          2nd Day Air
                        </span>
                      )}
                    </TableCell>
                    {verdictFilter === "all" ? (
                      <TableCell>
                        {v && Icon ? (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${v.color}`}
                            title={r.result?.error || r.result?.status || ""}>
                            <Icon className="w-3 h-3" /> {v.label}
                          </span>
                        ) : busyVerify ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                        ) : (
                          <span className="text-xs text-slate-300">pending</span>
                        )}
                      </TableCell>
                    ) : null}
                    <TableCell className="font-mono text-[11px] text-slate-600">
                      {r.verdict === "not_found"
                        ? <span className="text-amber-700">Manual review</span>
                        : (
                          <div className="flex flex-col leading-tight">
                            <span>{fmtTs(r.result?.received_at)}</span>
                            {r.result?.deadline_at ? (
                              <span className="text-[10px] text-slate-400">
                                → due {fmtTs(r.result.deadline_at)}
                              </span>
                            ) : null}
                          </div>
                        )}
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-600">
                      {r.verdict === "not_found"
                        ? <span className="text-amber-700 italic">UPS returned no scans</span>
                        : fmtTs(r.result?.delivered_at)}
                    </TableCell>
                    <TableCell className="font-mono text-right text-xs">
                      {r.verdict === "not_found" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-900 border-amber-300">
                          MANUAL REVIEW
                        </span>
                      ) : r.verdict === "in_transit" && stuckRows.includes(r) ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300">
                          STUCK
                        </span>
                      ) : r.verdict === "late" && (r.service_tier || "2nd_day_air") === "2nd_day_air" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-rose-100 text-rose-800 border-rose-300">
                          LATE
                        </span>
                      ) : r.result?.hours_in_transit != null
                        ? <span className={r.verdict === "late" ? "text-rose-700 font-bold" : ""}>{r.result.hours_in_transit}h</span>
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {visibleRows.length > 500 && (
            <div className="p-3 text-xs text-slate-500 border-t border-slate-100 text-center">
              Showing first 500 of {visibleRows.length} rows — export the PDF for the full list.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
